import { AdTypeEnum } from '@/__generated_graphql__/types';

import { BREAKPOINTS } from './breakpoints';

export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  UPDATE_PASSWORD: '/update-password',
  RESET_PASSWORD: '/reset-password',
  PROFILE: '/profile',
  TERMS_AND_CONDITIONS: '/pages/terms-conditions',
  VACANCIES: '/vacancies',
  VACANCY_PAY: (vacancyId: string, paymentType: string) =>
    `/vacancies/${vacancyId}/payment?paymentType=${paymentType}`,
  RESUME_PAY: (resumeId: string, paymentType: string) =>
    `/resumes/${resumeId}/payment?paymentType=${paymentType}`,
  RESUME: '/resume',
  RESULT: '/result',
  CREATE: '/create',
  VACANCIES_CREATE: '/vacancy/create',
  RESUME_CREATE: '/resume/create',
  BOOKMARKS: '/bookmarks',
  SITEMAP: '/pages/sitemap',
  ABOUT: '/pages/about',
  ADVERTISEMENTS: '/pages/advertising',
  SEARCH: '/search',
  SEARCH_VACANCIES: '/search/vacancies',
  SEARCH_RESUMES: '/search/resumes',
  RESUMES: '/resumes',
  CREATE_RESUME: '/create/resume',
  CREATE_VACANCY: '/create/vacancy',
  NOT_FOUND: '/not-found',
  COMPANIES: '/companies',
  VACANCY_DETAILS: (id?: string | number) => `/vacancies/${id}`,
  RESUME_DETAILS: (id?: string | number) => `/resumes/${id}`,
  PUBLISH_AD_INFO: (id: string, adType: AdTypeEnum) => {
    const params = new URLSearchParams({
      showModal: 'true',
      adId: id,
      adType
    });

    return `${ROUTES.PROFILE}?${params.toString()}`;
  },
  PRIVACY_POLICE: '/pages/privacy-policy',
  LISTING_GUIDELINES: '/pages/listing-guidelines',
  EMBED: '/embed'
} as const;

export const PARTICIPANT_ROUTES = [ROUTES.RESULT, ROUTES.CREATE];

export const AUTH_ROUTES = [
  ROUTES.LOGIN,
  ROUTES.UPDATE_PASSWORD,
  ROUTES.RESET_PASSWORD
];

export const STATUS_CODES = {
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  LIMIT_EXCEEDED: 'LIMIT_EXCEEDED',
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  SUCCESS: 'SUCCESS'
};

export const AD_TYPE = {
  RESUME: 'Resume',
  VACANCY: 'Vacancy'
} as const;

export const STORAGE_KEYS = {
  THEME: 'theme',
  LOCKOUT_END: 'lockout_end'
};

export const TIME_FORMAT = {
  FULL_DATE_TIME: 'D MMMM YYYY, HH:mm',
  TIME_ONLY: 'HH:mm',
  DATE_ONLY: 'DD.MM.YYYY'
};

export enum CardStatus {
  APPROVED = 'approved',
  REJECTED = 'rejected',
  PENDING = 'pending',
  EXPIRED = 'expired',
  AWAITING_PAYMENT = 'awaiting_payment'
}

export enum ParticipantEnum {
  VACANCY = 'VACANCY',
  RESUME = 'RESUME'
}

export enum LimitTypesEnum {
  MIN = 'MIN',
  MAX = 'MAX'
}

export const MAX_SIZE_MB = 5;
export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif'
];

export const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];
export const ALLOWED_EXT = ['.jpg', '.jpeg', '.png', '.webp'];

export const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

export const PAGE_HEADER_ID = 'page-header';

export const MAX_VISIBLE_CATEGORIES = 9;
export const MAX_VISIBLE_CATEGORIES_ON_TABLET = 6;

export const MAX_VISIBLE_VACANCIES = 8;
export const MIN_SALARY_VALUE = 0;
export const MAX_SALARY_VALUE = 80000;
export const MAX_VISIBLE_DEFAULT_SUGGESTIONS = 5;
export const MAX_VISIBLE_DEFAULT_CATEGORIES_SUGGESTIONS = 11;

export const MIN_TOPUP_CARD_AMOUNT = 1;
export const MAX_TOPUP_CARD_AMOUNT_PORTMANAT = 1000;
export const MAX_TOPUP_CARD_AMOUNT_YIGIM = 5000;

export const MIN_AGE_VALUE = 16;
export const MAX_AGE_VALUE = 65;
export const MIN_YEAR = 1900;
export const MAX_DESCRIPTION_VALUE = 4000;
export const MIN_PHONE_LENGTH = 7;
export const MAX_PHONE_LENGTH = 15;
export const MIN_MONTH_DAY = 1;
export const MAX_MONTH_DAY = 31;
export const ONLY_NUMBER_REG = /^\d*$/;
export const VALID_EMAIL_REG = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const VALID_PHONE_REG = /^\+?[0-9\s\-()]{7,}$/;
export const ONLY_NUMBER_PLUS_REG = /^\+?\d*$/;
export const ALLOW_NUMBERS_AND_PLUS_REGEX = /(?!^\+)[^0-9]/g;
export const YEAR_REGEX = /^\d{4}$/;
export const PHONE_MASK = '(099) 999-99-99';
export const EXTRACT_LAST_NUMBER_REGEX = /(\d+)(?!.*\d)/;
export const FREE_WORD_REGEX = /(?:^|_)free(?:_|$)/;

export const MIN_TEXT_LENGTH = 1;
export const MAX_TEXT_LENGTH = 80;
export const DEFAULT_AVATAR_SIZE = 120;

export const DESKTOP_AVATAR_SIZE = 95;
export const MOBILE_AVATAR_SIZE = 55;

export const PAGE_HISTORY_DEPTH = 2;

export const HEADER_BOOKMARKS_BUTTON_ID = 'header-bookmarks-button';

export const HEADER_AD_BUTTON_ID = 'header-ad-button';

export const EVENT_SEARCH_LOADING = 'search-loading-event';
export const EVENT_TOGGLE_HEADER = 'toggle-header-event';

export const RESUME_FREE_ACTIVATION_ID = 'resume_free';
export const VACANCY_FREE_ACTIVATION_ID = 'vacancy_free';

export const EXPERIENCES = 'experiences' as const;
export const EDUCATIONS = 'educations' as const;

export const DESKTOP_EMBEDED_ADS = 3;
export const MOBILE_EMBEDED_ADS = 2;

export type ModalPosition = 'bottom' | 'center' | 'bottom-half';

export enum FilterTypeEnum {
  RANGE = 'RANGE',
  MULTIPLE = 'MULTIPLE'
}

export enum NoAdsVariantEnum {
  RESULT = 'RESULT',
  PROFILE = 'ACCOUNT',
  NONE = 'NONE',
  BOOKMARKS = 'BOOKMARKS',
  COMPANY = 'COMPANY',
  SIMILAR_VACANCIES = 'SIMILAR_VACANCIES'
}

export enum PaymentType {
  BRUTTO = 'BRUTTO',
  NETTO = 'NETTO'
}

export const PARTICIPANT_SEGMENTS = {
  RESUMES: 'resumes',
  VACANCIES: 'vacancies'
} as const;

export type ParticipantSegments =
  (typeof PARTICIPANT_SEGMENTS)[keyof typeof PARTICIPANT_SEGMENTS];

export const PARTICIPANT_PARAM_MAP: Record<string, ParticipantEnum> = {
  resume: ParticipantEnum.RESUME,
  resumes: ParticipantEnum.RESUME,
  vacancy: ParticipantEnum.VACANCY,
  vacancies: ParticipantEnum.VACANCY
};

export enum TypeEnum {
  vacancy = 'vacancy',
  resume = 'resume'
}

export const COOKIES = {
  THEME: 'theme',
  PARTICIPANT: 'participant',
  AUTH_SESSION: '_bossaz_session'
} as const;

export const SERVICE_KEY_BY_TYPE = {
  BUMP: 'bump',
  FEATURED: 'featured'
} as const;

export type ServiceKey =
  (typeof SERVICE_KEY_BY_TYPE)[keyof typeof SERVICE_KEY_BY_TYPE];

export enum PaymentTypeEnum {
  ADVERTISE = 'ADVERTISE',
  PUBLICATION = 'PUBLICATION',
  TOP_UP_BALANCE = 'TOP_UP_BALANCE'
}

export type PaymentOption = {
  id: string;
  description: string;
  name: string;
  price: number;
  discount?: string;
  info?: string;
};
export type BreakpointsType = keyof typeof BREAKPOINTS;

export const PAYMENT_OPTIONS = {
  CARD: 'CARD',
  PORTMANAT: 'PORTMANAT',
  WALLET: 'WALLET'
} as const;

export type PaymentWayKey = keyof typeof PAYMENT_OPTIONS;
